// Loads the Spotfire Web Player JavaScript API (oauth.js) and opens an analysis
// using the OAuth 2.0 Authorization Code + PKCE flow (Spotfire 15.0 JS API).

import {
    spotfireClientId,
    spotfireOAuthJsPath,
    spotfireRedirectPath,
    spotfireApiVersion,
} from "config/settings";

let scriptPromise = null;
// setApiVersion may only be called once per page load.
let apiVersionSet = false;

const loadSpotfireApi = (server) => {
    if (window.spotfire?.webPlayer) {
        return Promise.resolve(window.spotfire.webPlayer);
    }
    if (scriptPromise) {
        return scriptPromise;
    }

    scriptPromise = new Promise((resolve, reject) => {
        const src = `${server}${spotfireOAuthJsPath}`;
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => {
            if (window.spotfire?.webPlayer) {
                resolve(window.spotfire.webPlayer);
            } else {
                scriptPromise = null;
                reject(new Error("Spotfire Web Player API failed to initialize"));
            }
        };
        script.onerror = () => {
            scriptPromise = null;
            reject(new Error("Failed to load Spotfire oauth.js"));
        };
        document.head.appendChild(script);
    });

    return scriptPromise;
};

// Normalizes a template detail link into a Spotfire library analysis path.
// e.g. "./Suppliers/Zollner/Analysis" -> "/Suppliers/Zollner/Analysis"
export const getAnalysisPath = (detailLink) => {
    if (!detailLink) {
        return "";
    }
    let path = detailLink.trim();
    if (path.startsWith(".")) {
        path = path.slice(1);
    }
    if (!path.startsWith("/")) {
        path = `/${path}`;
    }
    return path;
};

export const openSpotfireAnalysis = async ({ server, path, elementId }) => {
    const webPlayer = await loadSpotfireApi(server);

    if (!apiVersionSet) {
        webPlayer.setApiVersion(spotfireApiVersion);
        apiVersionSet = true;
    }

    const redirectUri = `${window.location.origin}${spotfireRedirectPath}`;
    const token = await webPlayer.authenticate({
        clientId: spotfireClientId,
        server,
        redirectUri,
    });

    const app = await webPlayer.connect({ token, path });
    const document = app.openDocument(elementId);
    return { app, document };
};
